import { useCallback, useMemo } from 'react'

import Handlebars from 'handlebars'

import { useQuery } from '@sequencehq/api'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'

type ResolutionBody =
  Dashboardv99990101Api.PostInvoiceReminderVariableResolution.Body

/**
 * Returns a resolver which replaces the Handlebars variables in a reminder
 * template string (body, subject or a recipient) with their values, as
 * resolved by the platform for the given invoice and customer. Returning a
 * function rather than a resolved string lets a single hook call resolve
 * every field of an email config.
 *
 * Templates pass through unresolved until both projections and the resolved
 * values are available.
 */
export const useTemplateResolver = ({
  invoice,
  customer
}: {
  invoice: ResolutionBody['invoice'] | undefined
  customer: ResolutionBody['customer'] | undefined
}): { resolveTemplate: (template: string) => string; isPending: boolean } => {
  // The invoice the reminder workflow captured is locked, but the customer's
  // contacts can change after capture. Re-fetch the live customer so variables
  // like contactName / contactEmail resolve against the latest data, falling
  // back to the captured snapshot if the fetch is unavailable or fails.
  const shouldFetchLiveCustomer = Boolean(invoice?.customerId)
  const customerQuery = useQuery(
    dashboardv99990101Client.getCustomer,
    { id: invoice?.customerId ?? '' },
    { enabled: shouldFetchLiveCustomer }
  )

  const resolutionCustomer = customerQuery.data ?? customer

  // The resolution cache key is (invoiceId, customerId) only, so swapping the
  // posted customer after the request fires would not re-resolve. Hold off
  // resolving while a live customer is genuinely in flight so the values
  // reflect it; when there is no id to fetch we use the snapshot immediately.
  const awaitingLiveCustomer =
    shouldFetchLiveCustomer && customerQuery.isPending

  // When there is nothing to resolve against (e.g. an intermediate node with
  // no invoice projection) the resolver just passes templates through, so it
  // is not "pending" — only a genuinely enabled, in-flight query counts.
  const valuesQueryEnabled = Boolean(
    invoice && !awaitingLiveCustomer && resolutionCustomer
  )
  const valuesQuery = useQuery(
    dashboardv99990101Client.postInvoiceReminderVariableResolution,
    // The cast covers the disabled state: the request never fires until
    // both projections are present
    { invoice, customer: resolutionCustomer } as ResolutionBody,
    {
      enabled: valuesQueryEnabled
    }
  )

  const values = useMemo(() => {
    if (!valuesQuery.data) {
      return undefined
    }

    // Variables the platform could not resolve (e.g. no primary contact, no
    // due date) come back as null and render as blanks
    return Object.fromEntries(
      Object.entries(valuesQuery.data).map(([key, value]) => [key, value ?? ''])
    )
  }, [valuesQuery.data])

  const resolve = useCallback(
    (template: string) =>
      values ? resolveTemplate(template, values) : template,
    [values]
  )

  return {
    isPending:
      awaitingLiveCustomer || (valuesQueryEnabled && valuesQuery.isPending),
    resolveTemplate: resolve
  }
}

export const resolveTemplate = (
  template: string,
  values: Record<string, string>
): string => {
  try {
    return Handlebars.compile(template)(values)
  } catch {
    return template
  }
}
